// Lieferungen im Frontend: Upload mit Fortschritt (XHR), Ordner aus Drag & Drop einsammeln, Anzeigetexte.
import type {
  DbPortalDokument,
  DbPortalEntgelt,
  DbPortalKomponente,
  DbPortalKomponentenArt,
  DbPortalKomponentenpreis,
  DbPortalKomponentenTabelle,
  DbPortalLieferung,
  DbPortalUploadErgebnis, DbPortalReferenzpreis } from '@gql/shared-types';
import { API_BASIS, ApiFehler } from '../api/client.js';

export const MAX_DATEI_MB = 100;
const MAX_JE_UPLOAD = 20;
const MAX_BYTES_JE_UPLOAD = 150 * 1024 * 1024;

/** Datei mit relativem Pfad (Ordner-Upload/ZIP-Struktur dient der Standortzuordnung). */
export interface Auswahl {
  datei: File;
  pfad: string;
}

export const ART_TEXT: Record<string, string> = {
  vertrag: 'Vertrag',
  rechnung: 'Rechnung',
  leistungsbeschreibung: 'Leistungsbeschreibung',
  agb: 'AGB / Vertragsbedingungen',
  entgeltaenderung: 'Entgeltänderung',
  sonstiges: 'Sonstiges',
};
export const DOK_STATUS_TEXT: Record<DbPortalDokument['status'], string> = {
  wartet: 'wartet auf Auswertung',
  in_arbeit: 'wird ausgewertet',
  erkannt: 'ausgewertet',
  fehler: 'nicht auswertbar',
};
export const LIEFER_STATUS_TEXT: Record<DbPortalLieferung['status'], string> = {
  offen: 'in Prüfung bei GQL',
  freigegeben: 'von GQL übernommen',
  abgelehnt: 'nicht übernommen',
  nachforderung: 'Nachforderung',
};
export const JN_TEXT: Record<DbPortalLieferung['jnStatus'], string> = {
  aus: 'JurNodes: Übertragung abgeschaltet',
  wartet: 'JurNodes: wird übertragen',
  trocken: 'JurNodes: Probelauf (nur Plan)',
  uebertragen: 'JurNodes: abgelegt',
  fehler: 'JurNodes: Übertragung fehlgeschlagen',
};
const ZEITRAUM_TEXT: Record<DbPortalEntgelt['zeitraum'], string> = {
  monat: 'monatlich',
  quartal: 'vierteljährlich',
  jahr: 'jährlich',
  einmalig: 'einmalig',
  unbekannt: '',
};

const EURO = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
export const euro = (cent: number) => EURO.format(cent / 100);
const PROZENT = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
/** 2.3 → „+2,3 %“ */
export const prozentText = (p: number) => `${p > 0 ? '+' : ''}${PROZENT.format(p)} %`;
export function entgeltText(e: DbPortalEntgelt): string {
  const betrag = e.betragCent !== null ? euro(e.betragCent) : e.betragText;
  return [betrag, ZEITRAUM_TEXT[e.zeitraum], e.steuer !== 'unbekannt' ? e.steuer : ''].filter(Boolean).join(' ');
}
export const KOMPONENTEN_TEXT: Record<DbPortalKomponentenArt, string> = {
  aufschaltung: 'Aufschaltung (Grundleistung)',
  leitung: 'Übertragungsweg/Leitung',
  uebertragungsgeraet: 'Übertragungsgerät',
  handfeuermelder: 'Handfeuermelder',
  schliesszylinder: 'Schließzylinder',
  router: 'Router',
  meldekriterium: 'Zusätzliches Meldekriterium',
  einsatzdatei: 'Einsatzdatei/Maßnahmeplan',
  erweiterungskarte: 'Erweiterungskarte',
  leitstellenkriterien: 'Kriterien zur privaten Leitstelle',
  stromversorgung: 'Stromversorgung',
  antenne: 'Antenne',
  konzessionsabgabe: 'Konzessionsabgabe',
  dienstleistung: 'Dienstleistung/Einrichtung',
  sonstiges: 'Sonstiges',
};
const KOMPONENTEN_REIHE = Object.keys(KOMPONENTEN_TEXT) as DbPortalKomponentenArt[];

/** Monatsmiete einer Komponentenzeile: „3,36 € (1,68 € je Stück, berechnet)“, „im Paketpreis „…““, „Preis nicht lesbar“ */
export function komponentenMieteText(z: DbPortalKomponente): string {
  if (z.preis === 'im_paket') return z.paket ? `im Paketpreis „${z.paket}“` : 'im Gesamtpreis enthalten';
  if (z.mieteCent === null) return z.preis === 'nicht_lesbar' ? 'Preis nicht lesbar' : '–';
  const jeStueck = z.einzelpreisCent !== null && z.menge !== null && z.menge > 1 ? ` (${euro(z.einzelpreisCent)} je Stück${z.einzelpreisBerechnet ? ', berechnet' : ''})` : '';
  return `${euro(z.mieteCent)}${jeStueck}`;
}

/** Tabellensumme mit Abgleich: „Summe 184,51 € – Zeilen stimmen“ */
export function komponentenSummeText(t: DbPortalKomponentenTabelle): string {
  const teile = [t.summe.cent !== null ? `Summe ${euro(t.summe.cent)} monatlich` : '', t.summeEinrichtung.cent !== null ? `Einrichtung ${euro(t.summeEinrichtung.cent)}` : ''].filter(Boolean);
  const abgleich = t.stimmig === null ? '' : t.stimmig ? 'Zeilen ergeben die Summe' : 'Zeilen ergeben die Summe nicht – bitte am Original prüfen';
  return [teile.join(' · '), abgleich].filter(Boolean).join(' – ');
}

export interface KomponentenStand {
  art: DbPortalKomponentenArt;
  bezeichnung: string;
  menge: number | null;
  mieteCent: number | null;
  einzelpreisCent: number | null;
  einrichtungCent: number | null;
  preis: DbPortalKomponente['preis'];
  paket: string | null;
  /** frühestes und spätestes Dokumentdatum mit diesem Preis */
  von: string | null;
  bis: string | null;
  unterlagen: number;
  /** alle Fundtabellen stimmig */
  stimmig: boolean;
}

/**
 * Komponentenpreise eines Standorts verdichten: gleiche Komponente zum gleichen Preis aus mehreren Unterlagen
 * (z. B. Quartalsrechnungen) als eine Zeile mit Zeitraum. Reihenfolge: Komponentenart, dann Datum.
 */
export function komponentenUebersicht(preise: DbPortalKomponentenpreis[]): KomponentenStand[] {
  const nachSchluessel = new Map<string, KomponentenStand & { dokumente: Set<string> }>();
  const norm = (t: string) => t.toLowerCase().replace(/[^a-z0-9äöüß]+/g, '');
  for (const p of preise) {
    const z = p.komponente;
    const schluessel = [z.art, z.typ ?? norm(z.bezeichnung), z.mieteCent, z.einrichtungCent, z.preis].join('|');
    const alt = nachSchluessel.get(schluessel);
    if (alt) {
      alt.dokumente.add(p.dokumentId);
      alt.unterlagen = alt.dokumente.size;
      if (p.datum && (!alt.von || p.datum < alt.von)) alt.von = p.datum;
      if (p.datum && (!alt.bis || p.datum > alt.bis)) alt.bis = p.datum;
      alt.stimmig &&= p.stimmig !== false;
      continue;
    }
    nachSchluessel.set(schluessel, {
      art: z.art,
      bezeichnung: z.bezeichnung,
      menge: z.menge,
      mieteCent: z.mieteCent,
      einzelpreisCent: z.einzelpreisCent,
      einrichtungCent: z.einrichtungCent,
      preis: z.preis,
      paket: z.paket,
      von: p.datum,
      bis: p.datum,
      unterlagen: 1,
      stimmig: p.stimmig !== false,
      dokumente: new Set([p.dokumentId]),
    });
  }
  return [...nachSchluessel.values()]
    .map(({ dokumente: _d, ...rest }) => rest)
    .sort((a, b) => KOMPONENTEN_REIHE.indexOf(a.art) - KOMPONENTEN_REIHE.indexOf(b.art) || (a.von ?? '9999').localeCompare(b.von ?? '9999'));
}

/** Maßgeblicher Referenz-Kaufpreis zu einer Komponentenart (markiert „primaer“, sonst der erste), null ohne Eintrag. */
export function referenzFuer(art: DbPortalKomponentenArt, liste: DbPortalReferenzpreis[] | undefined): DbPortalReferenzpreis | null {
  const passende = (liste ?? []).filter((r) => r.art === art);
  return passende.find((r) => r.primaer) ?? passende[0] ?? null;
}

/** Vielfaches des Kaufpreises, das die Monatsmiete je Stück über `monate` Monate erreicht (eine Nachkommastelle). */
export function mietVielfaches(mieteJeMonatCent: number | null, kaufpreisCent: number | null, monate = 120): number | null {
  if (mieteJeMonatCent === null || kaufpreisCent === null || kaufpreisCent <= 0) return null;
  return Math.round(((mieteJeMonatCent * monate) / kaufpreisCent) * 10) / 10;
}

/** „5,5-fach (416,40 € in 120 Monaten)“ – nur bei eigenem Preis je Stück und vorhandenem Referenzpreis */
export function vielfachesText(k: Pick<KomponentenStand, 'preis' | 'einzelpreisCent'>, ref: DbPortalReferenzpreis | null, monate = 120): string | null {
  if (k.preis === 'im_paket' || k.einzelpreisCent === null || !ref) return null;
  const v = mietVielfaches(k.einzelpreisCent, ref.preisCent, monate);
  if (v === null) return null;
  return `${v.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}-fach (${euro(k.einzelpreisCent * monate)} in ${monate} Monaten)`;
}

/** JJJJ-MM-TT → TT.MM.JJJJ */
export const datumText = (iso: string) => (/^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : iso);
export const zeitpunkt = (iso: string) =>
  `${new Date(iso).toLocaleString('de-DE', { timeZone: 'Europe/Berlin', dateStyle: 'medium', timeStyle: 'short' })} Uhr`;
export const groesse = (b: number) => (b < 1024 * 1024 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / 1024 / 1024).toFixed(1).replace('.', ',')} MB`);

/** Teilt die Auswahl in Uploads (höchstens 20 Dateien und 150 MB je Anfrage). */
export function inPakete(auswahl: Auswahl[]): Auswahl[][] {
  const pakete: Auswahl[][] = [];
  let aktuell: Auswahl[] = [];
  let bytes = 0;
  for (const a of auswahl) {
    if (aktuell.length && (aktuell.length >= MAX_JE_UPLOAD || bytes + a.datei.size > MAX_BYTES_JE_UPLOAD)) {
      pakete.push(aktuell);
      aktuell = [];
      bytes = 0;
    }
    aktuell.push(a);
    bytes += a.datei.size;
  }
  if (aktuell.length) pakete.push(aktuell);
  return pakete;
}

/** Ein Upload mit Fortschrittsanzeige (fetch kennt keinen Upload-Fortschritt). */
export function sendePaket(pfad: string, paket: Auswahl[], felder: Record<string, string>, fortschritt: (anteil: number) => void): Promise<DbPortalUploadErgebnis> {
  return new Promise((aufloesen, ablehnen) => {
    const fd = new FormData();
    for (const [k, v] of Object.entries(felder)) if (v) fd.append(k, v);
    for (const a of paket) fd.append('dateien', a.datei, a.pfad);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', API_BASIS + pfad);
    xhr.withCredentials = true;
    xhr.responseType = 'json';
    xhr.upload.onprogress = (e) => e.lengthComputable && fortschritt(e.loaded / e.total);
    xhr.onerror = () => ablehnen(new ApiFehler(0, 'netzwerk', 'Keine Verbindung zum Server. Bitte erneut versuchen.'));
    xhr.onload = () => {
      const daten = xhr.response as (DbPortalUploadErgebnis & { fehler?: string; meldung?: string }) | null;
      if (xhr.status >= 200 && xhr.status < 300 && daten) return aufloesen(daten);
      ablehnen(new ApiFehler(xhr.status, daten?.fehler ?? 'unbekannt', daten?.meldung ?? 'Der Upload ist fehlgeschlagen.'));
    };
    xhr.send(fd);
  });
}

/** Dateien aus Drag & Drop einsammeln, Ordner rekursiv (Pfad bleibt für die Standortzuordnung erhalten). */
export async function ausAblage(dt: DataTransfer): Promise<Auswahl[]> {
  const eintraege = [...dt.items].map((i) => i.webkitGetAsEntry?.()).filter((x): x is FileSystemEntry => !!x);
  if (!eintraege.length) return [...dt.files].map((datei) => ({ datei, pfad: datei.name }));
  const ergebnis: Auswahl[] = [];
  const lies = async (e: FileSystemEntry): Promise<void> => {
    if (e.isFile) {
      const datei = await new Promise<File>((ok, nein) => (e as FileSystemFileEntry).file(ok, nein));
      ergebnis.push({ datei, pfad: e.fullPath.replace(/^\//, '') });
    } else if (e.isDirectory) {
      const leser = (e as FileSystemDirectoryEntry).createReader();
      for (;;) {
        const teil = await new Promise<FileSystemEntry[]>((ok, nein) => leser.readEntries(ok, nein));
        if (!teil.length) break;
        for (const k of teil) await lies(k);
      }
    }
  };
  for (const e of eintraege) await lies(e);
  return ergebnis.filter((a) => !/(^|\/)\.[^/]*$/.test(a.pfad));
}

export const ausAuswahl = (liste: FileList | null): Auswahl[] =>
  [...(liste ?? [])].map((datei) => ({ datei, pfad: (datei as File & { webkitRelativePath?: string }).webkitRelativePath || datei.name }));
